import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { apiSend } from '../lib/api';
import { isThemeId, type ThemeId } from '../lib/themes';

const STORAGE_KEY = 'theme';

const readStoredTheme = (): ThemeId | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return isThemeId(raw) ? raw : null;
  } catch {
    return null;
  }
};

const initialTheme = (): ThemeId => {
  const saved = readStoredTheme();
  if (saved) return saved;
  if (typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'alpha';
};

export const useTheme = () => {
  const { getToken } = useAuth();
  const [theme, setLocalTheme] = useState<ThemeId>(initialTheme);
  const [syncing, setSyncing] = useState(false);

  // Apply to DOM + persist locally on every change.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // localStorage may be disabled — fall through, we still applied to DOM.
    }
  }, [theme]);

  const setTheme = useCallback(
    async (next: ThemeId) => {
      if (next === theme) return;
      setLocalTheme(next);
      setSyncing(true);
      try {
        const token = await getToken();
        await apiSend('PATCH', '/api/me/preferences', { theme: next }, token);
      } catch (err) {
        // Server sync is best-effort. The theme is still applied locally; the
        // next time /api/me loads on this device it'll re-apply from localStorage.
        // eslint-disable-next-line no-console
        console.warn('failed to persist theme to server', err);
      } finally {
        setSyncing(false);
      }
    },
    [theme, getToken],
  );

  return { theme, setTheme, syncing };
};
